import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  Row,
  Col,
  DatePicker,
  Statistic,
  message,
  Spin,
  Space,
  Button,
  Descriptions,
  Tag,
} from 'antd';
import {
  StarOutlined,
  RiseOutlined,
  FallOutlined,
  TrophyOutlined,
  ArrowLeftOutlined,
} from '@ant-design/icons';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import dayjs, { Dayjs } from 'dayjs';
import api from '../utils/api';

const { RangePicker } = DatePicker;

interface KPISummary {
  total_reviews: number;
  average_rating: number | null;
  count_5_star: number;
  count_1_star: number;
  share_4_5_star: number | null;
}

interface DailyTrendPoint {
  date: string;
  total: number;
  count_1: number;
  count_2: number;
  count_3: number;
  count_4: number;
  count_5: number;
  avg_rating: number | null;
}

interface StarDistribution {
  star_1: number;
  star_2: number;
  star_3: number;
  star_4: number;
  star_5: number;
}

interface StoreDetailInfo {
  store_id: number;
  store_name: string;
  store_code: string | null;
  region_id: number;
  region_name: string;
  google_location_name: string;
  current_rating?: string | null;  // Current total rating snapshot (fetched from Google API)
  is_active: boolean;
}

interface StoreDetailData {
  store_info: StoreDetailInfo;
  kpi: KPISummary;
  daily_trend: DailyTrendPoint[];
  star_distribution: StarDistribution;
}

const StoreDetailPage: React.FC = () => {
  const { storeId } = useParams<{ storeId: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<StoreDetailData | null>(null);
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>([
    dayjs().subtract(30, 'day'),
    dayjs(),
  ]);

  const fetchStoreDetail = useCallback(async () => {
    if (!storeId) {
      message.error('Invalid store ID');
      navigate('/dashboard');
      return;
    }

    setLoading(true);
    try {
      const params: any = {
        date_from: dateRange[0].format('YYYY-MM-DD'),
        date_to: dateRange[1].format('YYYY-MM-DD'),
      };
      
      const response = await api.get(`/dashboard/stores/${storeId}`, { params });
      setData(response.data);
    } catch (error: any) {
      message.error('Failed to fetch store details: ' + (error.response?.data?.detail || error.message));
      if (error.response?.status === 404) {
        navigate('/dashboard');
      }
    } finally {
      setLoading(false);
    }
  }, [storeId, dateRange, navigate]);

  useEffect(() => {
    fetchStoreDetail();
  }, [fetchStoreDetail]);

  const handleDateRangeChange = (dates: [Dayjs | null, Dayjs | null] | null) => {
    if (dates && dates[0] && dates[1]) {
      setDateRange([dates[0], dates[1]]);
    }
  };

  const handleQuickDateSelect = (days: number) => {
    setDateRange([dayjs().subtract(days, 'day'), dayjs()]);
  };

  // Prepare chart data
  const dailyTrendData = data?.daily_trend.map((point) => ({
    date: dayjs(point.date).format('MM/DD'),
    total: point.total,
    '1⭐': point.count_1,
    '2⭐': point.count_2,
    '3⭐': point.count_3,
    '4⭐': point.count_4,
    '5⭐': point.count_5,
  })) || [];

  const starDistributionData = data?.star_distribution ? [
    { name: '5⭐', value: data.star_distribution.star_5, color: '#52c41a' },
    { name: '4⭐', value: data.star_distribution.star_4, color: '#1890ff' },
    { name: '3⭐', value: data.star_distribution.star_3, color: '#faad14' },
    { name: '2⭐', value: data.star_distribution.star_2, color: '#fa8c16' },
    { name: '1⭐', value: data.star_distribution.star_1, color: '#f5222d' },
  ] : [];

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <Space>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/dashboard')}
          >
            Back to Dashboard
          </Button>
          <h1 style={{ margin: 0 }}>Store Details</h1>
        </Space>
      </div>

      <Spin spinning={loading}>
        {data && (
          <>
            {/* Store Information */}
            <Card style={{ marginBottom: '24px' }}>
              <Descriptions title="Store Information" bordered column={3}>
                <Descriptions.Item label="Store Name">{data.store_info.store_name}</Descriptions.Item>
                <Descriptions.Item label="Store Code">{data.store_info.store_code || '-'}</Descriptions.Item>
                <Descriptions.Item label="Region">{data.store_info.region_name}</Descriptions.Item>
                <Descriptions.Item label="Google Location Name" span={2}>
                  {data.store_info.google_location_name}
                </Descriptions.Item>
                <Descriptions.Item label="Current Rating">
                  {data.store_info.current_rating ? `${data.store_info.current_rating}⭐` : '-'}
                </Descriptions.Item>
                <Descriptions.Item label="Status">
                  <Tag color={data.store_info.is_active ? 'green' : 'red'}>
                    {data.store_info.is_active ? 'Active' : 'Inactive'}
                  </Tag>
                </Descriptions.Item>
              </Descriptions>
            </Card>

            {/* Date Range Filter */}
            <Card style={{ marginBottom: '24px' }}>
              <Space direction="vertical" style={{ width: '100%' }} size="middle">
                <div>
                  <div style={{ marginBottom: '8px' }}>Date Range</div>
                  <RangePicker
                    value={dateRange}
                    onChange={handleDateRangeChange}
                    style={{ width: '100%', maxWidth: '400px' }}
                  />
                  <Space style={{ marginTop: '8px', marginLeft: '16px' }}>
                    <Button size="small" onClick={() => handleQuickDateSelect(0)}>Today</Button>
                    <Button size="small" onClick={() => handleQuickDateSelect(1)}>Yesterday</Button>
                    <Button size="small" onClick={() => handleQuickDateSelect(7)}>Last 7 Days</Button>
                    <Button size="small" onClick={() => handleQuickDateSelect(30)}>Last 30 Days</Button>
                  </Space>
                </div>
              </Space>
            </Card>

            {/* KPI Cards */}
            <Row gutter={16} style={{ marginBottom: '24px' }}>
              <Col span={6}>
                <Card>
                  <Statistic
                    title="Total Reviews"
                    value={data.kpi.total_reviews}
                    prefix={<StarOutlined />}
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card>
                  <Statistic
                    title="Average Rating"
                    value={
                      data.kpi.average_rating === null || data.kpi.average_rating === undefined
                        ? 0
                        : typeof data.kpi.average_rating === 'string'
                        ? parseFloat(data.kpi.average_rating) || 0
                        : data.kpi.average_rating
                    }
                    precision={2}
                    prefix={<TrophyOutlined />}
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card>
                  <Statistic
                    title="5⭐ Reviews"
                    value={data.kpi.count_5_star}
                    prefix={<RiseOutlined />}
                    valueStyle={{ color: '#52c41a' }}
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card>
                  <Statistic
                    title="1⭐ Reviews"
                    value={data.kpi.count_1_star}
                    prefix={<FallOutlined />}
                    valueStyle={{ color: '#f5222d' }}
                  />
                </Card>
              </Col>
            </Row>

            {/* Charts */}
            <Row gutter={16} style={{ marginBottom: '24px' }}>
              <Col span={16}>
                <Card title="Daily Review Trend">
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={dailyTrendData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" interval={0} angle={-45} textAnchor="end" height={50} />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Line type="monotone" dataKey="total" stroke="#1890ff" strokeWidth={2} />
                    </LineChart>
                  </ResponsiveContainer>
                </Card>
              </Col>
              <Col span={8}>
                <Card title="Star Distribution">
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={starDistributionData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {starDistributionData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </Card>
              </Col>
            </Row>

            <Row gutter={16} style={{ marginBottom: '24px' }}>
              <Col span={24}>
                <Card title="Daily Star Distribution (Stacked Bar Chart)">
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={dailyTrendData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" interval={0} angle={-45} textAnchor="end" height={50} />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="5⭐" stackId="a" fill="#52c41a" />
                      <Bar dataKey="4⭐" stackId="a" fill="#1890ff" />
                      <Bar dataKey="3⭐" stackId="a" fill="#faad14" />
                      <Bar dataKey="2⭐" stackId="a" fill="#fa8c16" />
                      <Bar dataKey="1⭐" stackId="a" fill="#f5222d" />
                    </BarChart>
                  </ResponsiveContainer>
                </Card>
              </Col>
            </Row>
          </>
        )}
      </Spin>
    </div>
  );
};

export default StoreDetailPage;
